// THE BATCHED NAME SOURCE behind `RefLabel` (design-system §3.9, spec 16
// §3.13; D-441).
//
// A screen hands `RefLabel` a kind and an id. Every label that mounts in the
// same tick asks this loader, the loader collects the asks for one macrotask
// and sends them as ONE `GET /v1/refs` (split at the contract's cap of 100),
// and React Query caches each answer per kind and id, so a page of 50 rows
// that name 12 people and 30 drums costs one request, and the next page costs
// only the ids it has not seen.
//
// What the server leaves out (an id the reader may not see, or one that does
// not exist) is cached as `null`, which `RefLabel` renders as the short-id
// chip. The two cases are deliberately indistinguishable (spec 16 §3.13).
//
// A NAME BELONGS TO THE SESSION THAT READ IT (spec 16 §3.13 threat 6). Sign-in
// and sign-out are in-app navigations and the one QueryClient outlives them,
// so on a shared office PC a group admin's cached co-worker names would answer
// the next person's labels for the whole stale window without asking the
// server. Every key carries the session epoch, `resetRefNames` moves the epoch
// and drops the cached names on sign-in and on sign-out, and a batch that was
// in flight across the change resolves to nothing rather than to the previous
// reader's names.

import { useQueries, useQuery, type QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { REFS_BATCH_MAX } from "@assessly/contracts";
import type { components } from "@assessly/api-client";
import { client } from "./api";

export type RefKind = components["schemas"]["RefKind"];
type RefItem = components["schemas"]["RefLabelItem"];

export { REFS_BATCH_MAX };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when `id` can be asked about at all; anything else is a chip at once. */
export function isRefId(id: string | null | undefined): id is string {
  return typeof id === "string" && UUID.test(id);
}

export type RefAnswer = Pick<RefItem, "label" | "code" | "label_i18n"> | null;

type Fetcher = (refs: string[]) => Promise<RefItem[]>;

const defaultFetcher: Fetcher = async (refs) => {
  const { data, error } = await client.GET("/v1/refs", { params: { query: { ref: refs } } });
  if (data === undefined) throw new Error(`refs: ${JSON.stringify(error)}`);
  return data.items;
};

interface Waiter {
  resolve: (answer: RefAnswer) => void;
  reject: (err: unknown) => void;
}

/**
 * Collects the asks of one macrotask and answers them in as few requests as
 * the cap allows. Exported as a class so a test can drive one with a fake
 * fetcher; the app uses the shared `refLoader`.
 */
export class RefLoader {
  private pending = new Map<string, Waiter[]>();
  private scheduled = false;
  private generation = 0;

  constructor(private readonly fetcher: Fetcher = defaultFetcher) {}

  /**
   * Forget the session: asks not yet sent resolve to nothing, and a batch
   * already in flight resolves to nothing when it lands, so no name read for
   * one session reaches a label of the next.
   */
  reset(): void {
    this.generation += 1;
    const dropped = this.pending;
    this.pending = new Map();
    for (const waiters of dropped.values()) for (const waiter of waiters) waiter.resolve(null);
  }

  load(kind: RefKind, id: string): Promise<RefAnswer> {
    const key = `${kind}:${id.toLowerCase()}`;
    return new Promise<RefAnswer>((resolve, reject) => {
      const waiters = this.pending.get(key);
      if (waiters === undefined) this.pending.set(key, [{ resolve, reject }]);
      else waiters.push({ resolve, reject });
      if (!this.scheduled) {
        this.scheduled = true;
        setTimeout(() => void this.flush(), 0);
      }
    });
  }

  private async flush(): Promise<void> {
    const batch = this.pending;
    const generation = this.generation;
    this.pending = new Map();
    this.scheduled = false;
    const keys = [...batch.keys()];
    for (let i = 0; i < keys.length; i += REFS_BATCH_MAX) {
      const chunk = keys.slice(i, i + REFS_BATCH_MAX);
      try {
        const fetched = await this.fetcher(chunk);
        // The session changed while this batch was in flight: its names were
        // read for someone else.
        const items = generation === this.generation ? fetched : [];
        const byKey = new Map(items.map((item) => [`${item.kind}:${item.id.toLowerCase()}`, item]));
        for (const key of chunk) {
          const item = byKey.get(key);
          const answer: RefAnswer =
            item === undefined
              ? null
              : {
                  label: item.label,
                  ...(item.code !== undefined ? { code: item.code } : {}),
                  ...(item.label_i18n !== undefined ? { label_i18n: item.label_i18n } : {}),
                };
          for (const waiter of batch.get(key) ?? []) waiter.resolve(answer);
        }
      } catch (err) {
        for (const key of chunk) for (const waiter of batch.get(key) ?? []) waiter.reject(err);
      }
    }
  }
}

export const refLoader = new RefLoader();

/** Pick the reader's language from a work centre's bundle, else the label. */
export function refText(answer: RefAnswer, language: string): string | undefined {
  if (answer === null) return undefined;
  const bundle = answer.label_i18n as Record<string, string | undefined> | undefined;
  if (bundle !== undefined) {
    const exact = bundle[language] ?? bundle[language.split("-")[0] ?? ""];
    if (exact !== undefined && exact.length > 0) return exact;
  }
  return answer.label;
}

/** Moves on every sign-in and sign-out; part of every name's cache key. */
let refSession = 0;

export const refNameKey = (kind: RefKind, id: string) =>
  ["ref-name", refSession, kind, id.toLowerCase()] as const;

/**
 * Called on sign-in and on sign-out (routes/login.tsx, routes/root.tsx,
 * routes/reset-password.tsx): the next reader on this browser asks the server
 * for every name again, under their own grants.
 */
export function resetRefNames(queryClient: QueryClient | undefined): void {
  refSession += 1;
  refLoader.reset();
  queryClient?.removeQueries({ queryKey: ["ref-name"] });
}

/**
 * The name of one reference: `name` once resolved (undefined when the server
 * did not name it), `pending` while the batch is in flight. An id that is not
 * a uuid is never asked about.
 */
export function useRefName(
  kind: RefKind,
  id: string | null | undefined,
): { name: string | undefined; pending: boolean } {
  const { i18n } = useTranslation();
  const enabled = isRefId(id);
  const query = useQuery({
    queryKey: refNameKey(kind, enabled ? id : ""),
    queryFn: () => refLoader.load(kind, enabled ? id : ""),
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    // ALWAYS, not the default "online": offline, an "online" query PAUSES and
    // stays pending, so every label would read "Looking up the name…" for as
    // long as there is no signal. Asked anyway, the read fails at once and the
    // label shows the short-id chip, as §3.9 promises for a failed read.
    networkMode: "always",
  });
  if (!enabled) return { name: undefined, pending: false };
  return {
    name: query.data === undefined ? undefined : refText(query.data, i18n.language),
    pending: query.isPending,
  };
}

/**
 * True once every name `ids` would ask for has answered, named or not. A
 * region that reveals all of its rows at once holds its reveal on this
 * (design-system §5): the resolving marker is narrower than a name, so a
 * column revealed before its names land widens after first paint. The asks
 * share `useRefName`'s keys and loader, so a list that also renders the
 * labels costs no second request. `kind` undefined asks nothing and is
 * settled at once, as is a failed read: the label shows its chip then.
 */
export function useRefNamesSettled(
  kind: RefKind | undefined,
  ids: readonly (string | null | undefined)[],
): boolean {
  const asked =
    kind === undefined ? [] : [...new Set(ids.filter(isRefId).map((id) => id.toLowerCase()))];
  const results = useQueries({
    queries: asked.map((id) => ({
      queryKey: refNameKey(kind ?? "user", id),
      queryFn: () => refLoader.load(kind ?? "user", id),
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      retry: false,
      refetchOnWindowFocus: false,
      networkMode: "always" as const,
    })),
  });
  return results.every((result) => !result.isPending);
}
